import { ReactNode } from 'react';

import { useBaseAuth, TurnstileProvider } from '@cloudquery/base-users';
import { registerFetchErrorListener } from '@cloudquery/custom-fetch';
import CssBaseline from '@mui/material/CssBaseline';

import { ThemeProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';

import { BrowserRouter } from 'react-router';
import { NProgressStyles, Toaster, useMount } from 'shared/base';

import { AuthProvider } from '@/context/auth';
import { TrackingConsentProvider } from '@/context/trackingConsent';
import { AuthGuard } from '@/guards/auth';
import { theme } from '@/theme';

import { MSWProvider } from '@/utils/msw';
import { queryClient } from '@/utils/queryClient';

import { ConditionalRoutesGuard } from './conditionalRoutesGuard';
import { PlatformFeatureFlagsProvider } from './platformFeatureFlagsProvider';
import { PlatformVersionUpgrade } from './platformVersionUpgrade';
import { RouterErrorBoundary } from './routerErrorBoundary';
import { Tracking } from './tracking';
import { MfaProvider } from '../mfa/providers/mfaProvider';
import { NewVersionUpdater } from '../newVersionUpdater';

interface Props {
  children: ReactNode;
}

export function Providers({ children }: Props) {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NProgressStyles />
        <RouterErrorBoundary>
          <MSWProvider>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <TurnstileProvider>
                  <ApiErrorListener>
                    <PlatformFeatureFlagsProvider>
                      <AuthGuard>
                        <MfaProvider>
                          <ConditionalRoutesGuard>
                            {children}
                            <PlatformVersionUpgrade />
                          </ConditionalRoutesGuard>
                        </MfaProvider>
                      </AuthGuard>
                      <TrackingConsentProvider>
                        <Tracking />
                      </TrackingConsentProvider>
                    </PlatformFeatureFlagsProvider>
                  </ApiErrorListener>
                </TurnstileProvider>
              </AuthProvider>
              <Toaster />
              <NewVersionUpdater />
            </QueryClientProvider>
          </MSWProvider>
        </RouterErrorBoundary>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function ApiErrorListener({ children }: Props) {
  const { setLogoutState } = useBaseAuth();

  useMount(() => {
    registerFetchErrorListener((error) => {
      if ([401, 205].includes(error?.status)) {
        setLogoutState();
      }
    });
  });

  return <>{children}</>;
}
